// ============================================================================
// Chatbot knowledge base — Tharak Koneni
// ----------------------------------------------------------------------------
// HOW TO UPDATE
// This is the single source of truth for the chatbot.
// To change what the chatbot says, edit the `intents` array below or add a
// new entry. Each intent has:
//   - id:        unique string id
//   - keywords:  words/phrases that, when found in the user's question,
//                count toward this intent (lowercase, multi-word allowed)
//   - reply:     the answer text (kept short and factual — accuracy first)
//   - links:     optional [{ label, href }] anchors shown as chips
//   - followups: optional list of follow-up suggestions
//   - priority:  optional integer (default 0). Higher = stronger preference
//                when scores tie.
//
// Design principles:
//   1. Accuracy over verbosity. Replies are short and factual.
//   2. Any project question → GitHub redirect. We never describe individual
//      projects on the site; visitors are sent to GitHub for detail.
//   3. The matcher (matcher.js) handles synonyms, typos, and natural
//      phrasings — so you don't need to enumerate every variation here.
// ============================================================================

export const PROFILE = {
  name: 'Tharak Koneni',
  role: 'Developer',
  github: 'https://github.com/tah-rak',
  linkedin: 'https://www.linkedin.com/in/tharak-k',
  email: 'tharak.env@gmail.com',
  resume:
    'https://drive.google.com/file/d/1Coj9Eaclub3kCnhb7x1bawN1OF92mz5R/view?usp=drive_link',
};

export const GREETING = {
  text:
    "Hi! I'm Tharak's portfolio assistant. Ask me about his background, experience, research, skills, or how to get in touch.",
  suggestions: [
    'Tell me about Tharak',
    'What is he working on now?',
    'What technologies does he use?',
    'How can I contact him?',
    'Show me his projects',
  ],
};

// Used when the matcher is not confident in any intent.
export const FALLBACK = {
  reply:
    "I want to give you an accurate answer, so I'll keep it short. The most useful places to look are Tharak's GitHub for his work and email for direct questions.",
  links: [
    { label: 'email tharak', href: `mailto:${PROFILE.email}` },
    { label: 'github profile', href: PROFILE.github },
  ],
  followups: [
    'Tell me about Tharak',
    'What is he working on now?',
    'What technologies does he use?',
    'How can I contact him?',
  ],
};

// Unified GitHub redirect — used for ALL project / repo / code questions.
const GITHUB_REDIRECT = {
  reply:
    "You can explore all of Tharak's projects and repositories on GitHub here: https://github.com/tah-rak. Feel free to browse the repositories for detailed documentation, source code, and project information.",
  links: [{ label: 'open github profile', href: PROFILE.github }],
  followups: [
    'What is he working on now?',
    'What technologies does he use?',
    'How can I contact him?',
  ],
};

export const intents = [
  // ---------- conversational ----------
  {
    id: 'greeting',
    priority: 0,
    keywords: [
      'hi', 'hello', 'hey', 'yo', 'hola', 'sup', 'howdy', 'greetings',
      'good morning', 'good afternoon', 'good evening',
    ],
    reply:
      "Hey there. I can answer questions about Tharak's background, work, research, skills, or how to reach him. What would you like to know?",
    followups: ['Tell me about Tharak', 'What is he working on now?', 'How can I contact him?'],
  },
  {
    id: 'thanks',
    priority: 0,
    keywords: ['thanks', 'thank you', 'thx', 'ty', 'appreciate', 'cheers'],
    reply: "You're welcome. Anything else you'd like to know?",
    followups: ['Tell me about his experience', 'Show me his projects', 'How can I contact him?'],
  },
  {
    id: 'bye',
    priority: 0,
    keywords: ['bye', 'goodbye', 'see you', 'see ya', 'later', 'cya', 'take care'],
    reply: "Take care. Feel free to reach out anytime.",
    followups: ['How can I contact him?'],
  },
  {
    id: 'help',
    priority: 0,
    keywords: ['help', 'what can you do', 'what can you answer', 'what do you know', 'capabilities'],
    reply:
      "I can answer questions about Tharak's background, education, experience, current research, skills and technologies, achievements, publications, and how to contact him. For projects, I'll point you to his GitHub where you can see everything in detail.",
    followups: ['Tell me about Tharak', 'What technologies does he use?', 'Show me his projects'],
  },

  // ---------- about / identity ----------
  {
    id: 'about',
    priority: 2,
    keywords: [
      'who is tharak', 'who is he', 'about tharak', 'about him', 'about you',
      'who are you about', 'introduce tharak', 'introduce him', 'introduction',
      'background', 'bio', 'biography', 'profile', 'his story', 'about',
    ],
    reply:
      "Tharak is a developer who works at the intersection of research and production software. He focuses on language models, data pipelines, and the small details that decide whether a system holds up in real use. He is currently a graduate researcher at the University of South Florida.",
    followups: [
      'What is he working on now?',
      'What technologies does he use?',
      'Tell me about his experience',
    ],
  },
  {
    id: 'name_meaning',
    priority: 1,
    keywords: ['his name', 'what is his name', "what's his name", 'whats his name'],
    reply: "His name is Tharak Koneni. He goes by Tharak.",
    followups: ['Tell me about Tharak', 'How can I contact him?'],
  },
  {
    id: 'role',
    priority: 1,
    keywords: [
      'what does he do', 'what is his role', "what's his role", 'what is his job',
      'his title', 'his profession', 'what is he', "what's his profession",
      'what is his current role', 'current role',
    ],
    reply: "Tharak is a Developer. Right now he is a Graduate Researcher at the University of South Florida.",
    followups: ['What is he working on now?', 'Tell me about his experience'],
  },
  {
    id: 'location',
    priority: 1,
    keywords: [
      'where is he', 'where does he live', 'where is he based', 'his location',
      'where from', 'based in', 'city', 'country',
    ],
    reply:
      "Tharak prefers to keep his location off this site. The best way to reach him is by email or LinkedIn.",
    links: [
      { label: 'email tharak', href: `mailto:${PROFILE.email}` },
      { label: 'linkedin', href: PROFILE.linkedin },
    ],
    followups: ['How can I contact him?'],
  },

  // ---------- current work / research ----------
  {
    id: 'current_work',
    priority: 3,
    keywords: [
      'current', 'currently', 'right now', 'these days', 'present',
      'now', 'latest', 'today', 'this year', 'recent work',
      'what is he doing', 'what is he working on', 'what is he up to',
      'working on now', 'current research', 'current project',
      'what is he researching', 'his research', 'doing now',
    ],
    reply:
      "Right now he is a Graduate Researcher at the USF School of Information. He runs fine-tuning and evaluation cycles on language models (scoring outputs with BLEU, ROUGE, and BERTScore), adds retrieval and safety-validation layers so models can ground their answers in source material, and builds reproducible experiment pipelines and dashboards for the team.",
    followups: [
      'What research has he done before?',
      'What technologies does he use?',
      'Show me his projects',
    ],
  },
  {
    id: 'research_history',
    priority: 2,
    keywords: [
      'past research', 'previous research', 'research history', 'research before',
      'his past research', 'research he has done', 'usf research',
      'university of south florida', 'school of information', 'public health',
      'college of medicine', 'epidemiology', 'genomics', 'seir', 'shap',
    ],
    reply:
      "Tharak has worked across three USF labs. The College of Medicine (Aug 2024 to Dec 2024) optimized genomics pipelines and built classifiers for T-cell and B-cell identification. The College of Public Health (Jan 2025 to Jul 2025) trained predictive models on epidemiological time-series and simulated SEIR infection dynamics. He is now at the School of Information (Aug 2025 to Present) working on LLM fine-tuning, evaluation, and safety.",
    followups: ['Tell me about his publications', 'What technologies does he use?'],
  },

  // ---------- experience ----------
  {
    id: 'experience',
    priority: 2,
    keywords: [
      'experience', 'work history', 'jobs', 'roles', 'past work',
      'previous jobs', 'companies', 'employment', 'career', 'past roles',
      'professional experience', 'tell me about his experience',
      'his experience', 'his work',
    ],
    reply:
      "Tharak has shipped work across four roles: Graduate Researcher at the USF School of Information (LLM fine-tuning and evaluation), Research Assistant at the USF College of Public Health (epidemiological time-series modeling), Research Assistant at the USF College of Medicine (genomics pipelines and classifiers), and Data Science Intern at Crystal Experts Inc. (production Spring Boot and Node.js microservices on AWS).",
    followups: [
      'What did he do at Crystal Experts?',
      'What is he working on now?',
      'Tell me about his publications',
    ],
  },
  {
    id: 'crystal_experts',
    priority: 3,
    keywords: [
      'crystal', 'crystal experts', 'industry experience', 'data science intern',
      'internship', 'his internship',
    ],
    reply:
      "At Crystal Experts Inc. (Feb 2024 to Jun 2024), Tharak turned ML prototypes into production-grade Spring Boot and Node.js microservices on AWS ECS with Docker. He set up Jenkins and AWS CodeBuild CI/CD that cut inference defects by 70% and designed ETL workflows from MySQL and Cassandra into Snowflake, boosting throughput by 40%.",
    followups: ['What about his USF research?', 'What technologies does he use?'],
  },
  {
    id: 'years_experience',
    priority: 2,
    keywords: [
      'how many years', 'years of experience', 'how long has he', 'how long has he been',
      'his experience years',
    ],
    reply:
      "Tharak has more than four years of combined research and industry experience spanning ML, NLP, and data engineering.",
    followups: ['Tell me about his experience', 'What technologies does he use?'],
  },

  // ---------- skills / tech ----------
  {
    id: 'skills',
    priority: 2,
    keywords: [
      'skill', 'skills', 'technology', 'technologies', 'tech', 'tech stack',
      'stack', 'language', 'languages', 'framework', 'frameworks', 'tools',
      'what does he use', 'work with', 'works with', 'familiar with',
      'proficient in', 'his stack',
    ],
    reply:
      "Languages: Python, Java, C/C++, JavaScript. Frameworks: PyTorch, TensorFlow, React, Spring Boot, Node.js, Flask. Cloud and infra: AWS, Azure, Terraform, Firebase, Snowflake, Databricks. Databases: PostgreSQL, MongoDB, MySQL, Cassandra. Tools: Docker, Git, Jenkins, Airflow.",
    followups: ['What is he working on now?', 'Show me his projects'],
  },
  {
    id: 'focus_areas',
    priority: 2,
    keywords: [
      'focus', 'expertise', 'specialty', 'specialize', 'specialism',
      'good at', 'strengths', 'interests', 'professional interests',
      'areas of focus', 'what is he interested in',
    ],
    reply:
      "His focus areas are machine learning, natural language processing, MLOps and pipelines, research engineering, data engineering, predictive modeling, and LLM evaluation.",
    followups: ['What is he working on now?', 'What technologies does he use?'],
  },

  // ---------- projects ----------
  // All project-related questions route through this single intent.
  // We deliberately do NOT describe individual projects in the chatbot —
  // visitors are sent to GitHub where the latest documentation lives.
  {
    id: 'projects',
    priority: 3,
    keywords: [
      'project', 'projects', 'his projects', 'side project', 'side projects',
      'personal project', 'personal projects', 'portfolio of projects',
      'his work', 'work he has built', 'work he has done',
      'rag', 'rag project', 'rag bot', 'rag-bot', 'retrieval augmented',
      'knowledge conflict', 'langchain', 'vector db', 'pinecone', 'faiss',
      'reranking', 'reranker', 'hallucination project',
      'safety project', 'sensitive content', 'suicide detection',
      'suicidal ideation', 'content detection project', 'bert project',
      'ai safety', 'transformers project',
      'show me his work', 'show me his projects', 'show me his repos',
      'his code', 'source code', 'his github projects',
      'find his projects', 'find his work', 'where are his projects',
      'where can i see his projects', 'where can i find his projects',
      'where can i find his github projects',
      'built', 'building', 'made', 'created', 'developed',
      'what has he built', 'what has he made', 'what has he developed',
      'repos', 'repositories', 'open source', 'open-source',
    ],
    reply:
      "You can explore all of Tharak's projects and repositories on GitHub here: https://github.com/tah-rak. Feel free to browse the repositories for detailed documentation, source code, and project information.",
    links: [{ label: 'open github profile', href: PROFILE.github }],
    followups: [
      'What is he working on now?',
      'What technologies does he use?',
      'How can I contact him?',
    ],
  },
  {
    id: 'github',
    priority: 2,
    keywords: [
      'github', 'his github', 'github profile', 'github account', 'github link',
    ],
    reply: GITHUB_REDIRECT.reply,
    links: GITHUB_REDIRECT.links,
    followups: GITHUB_REDIRECT.followups,
  },

  // ---------- contact ----------
  {
    id: 'contact',
    priority: 3,
    keywords: [
      'contact', 'contact him', 'reach', 'reach out', 'reach him',
      'get in touch', 'get hold of', 'how can i contact', 'how do i contact',
      'how can i reach', 'how do i reach', 'how to reach',
      'email', 'his email', 'mail', 'phone', 'his phone',
      'message him', 'send him a message', 'connect with him',
    ],
    reply:
      "The fastest way is email. You can also reach him on LinkedIn or use the contact form at the bottom of the page.",
    links: [
      { label: 'email tharak', href: `mailto:${PROFILE.email}` },
      { label: 'linkedin', href: PROFILE.linkedin },
    ],
    followups: ['Is he open to new roles?', 'Show me his resume'],
  },
  {
    id: 'linkedin',
    priority: 2,
    keywords: ['linkedin', 'linked in', 'his linkedin', 'connect on linkedin'],
    reply: "Here is Tharak's LinkedIn profile.",
    links: [{ label: 'open linkedin', href: PROFILE.linkedin }],
    followups: ['How can I contact him?'],
  },
  {
    id: 'availability',
    priority: 3,
    keywords: [
      'available', 'open to', 'hiring', 'looking for work', 'looking for a job',
      'opportunities', 'job', 'jobs', 'internship', 'internships',
      'full time', 'fulltime', 'full-time', 'freelance', 'consulting',
      'collaborate', 'collaboration', 'is he hiring', 'is he available',
      'is he open', 'open for opportunities', 'open to work',
    ],
    reply:
      "Yes, Tharak is open to research collaborations, ML/AI roles, and full-time positions. The quickest way to start a conversation is email.",
    links: [{ label: 'email tharak', href: `mailto:${PROFILE.email}` }],
    followups: ['Show me his resume', 'What technologies does he use?'],
  },

  // ---------- education ----------
  {
    id: 'education',
    priority: 3,
    keywords: [
      'education', 'school', 'university', 'college', 'degree', 'degrees',
      'masters', "master's", 'masters degree', 'ms', 'm.s.',
      'bachelor', 'bachelors', "bachelor's", 'bachelors degree', 'be', 'b.e.',
      'gpa', 'studied', 'study', 'coursework', 'his coursework',
      'where did he study', 'where did he go to school',
      'nhce', 'new horizon', 'usf education',
    ],
    reply:
      "Tharak is pursuing his M.S. in Computer Science at the University of South Florida (Aug 2024 to May 2026, GPA 3.61/4.0). Before that, he earned a B.E. in Computer Science at New Horizon College of Engineering (Jul 2020 to Jun 2024, GPA 8.46/10).",
    followups: ['What research is he doing at USF?', 'What technologies does he use?'],
  },

  // ---------- publications ----------
  {
    id: 'publications',
    priority: 3,
    keywords: [
      'publication', 'publications', 'paper', 'papers', 'research paper',
      'research papers', 'ieee', 'ijsrcseit', 'journal', 'published',
      'his papers', 'has he published',
    ],
    reply:
      "Tharak has two peer-reviewed papers so far. 'Smart Predictive Enhancement for Safeguarding Well-being Using Machine Learning' (IEEE ICRISST 2024) and 'NCS in Security, Based on Self Organizing Maps' (IJSRCSEIT 2023). Both are linked in the Writing section of the site.",
    followups: ['What is his current research?', 'Tell me about his experience'],
  },

  // ---------- achievements ----------
  {
    id: 'achievements',
    priority: 2,
    keywords: [
      'achievement', 'achievements', 'awards', 'award', 'hackathon', 'hackathons',
      'wins', 'won', 'editorial', 'leadership', 'his achievements',
      'his awards', 'what has he won',
    ],
    reply:
      "Tharak led the editorial team of his college magazine (10 members) and won two NHCE hackathons: the Quantum Hackathon with a pre-built resume-templates platform, and the Idea Hackathon with a system to improve payment security in public transport.",
    followups: ['What technologies does he use?', 'How can I contact him?'],
  },

  // ---------- resume ----------
  {
    id: 'resume',
    priority: 3,
    keywords: [
      'resume', 'cv', 'curriculum vitae', 'download resume', 'his resume',
      'pdf resume', 'see his resume', 'show his resume',
    ],
    reply: "You can download Tharak's resume directly. The link below opens it in a new tab.",
    links: [{ label: 'open resume', href: PROFILE.resume }],
    followups: ['How can I contact him?', 'Tell me about his experience'],
  },
];
